/**
 * Where the client-facing forms are served from, and how the app knows it is
 * being asked for one.
 *
 * The dashboard answers on its own address — `app.naurist.com` in production,
 * `naurist.openuidev.com` on staging. The forms a practitioner shares answer on
 * a subdomain of their own, and the wildcard that catches those —
 * `*.naurist.com` and `*.naurist.openuidev.com` — is served by the same server
 * off the same bundle. Which of the two products exists is decided by the host
 * in the address bar, not by the build: a subdomain is somebody's forms, and
 * the dashboard's own address stays the dashboard.
 *
 * `connect.naurist.com/priya-nutrition`, the shared host with the name in the
 * path, is the older shape and still answers, for links already in the world.
 *
 * Resolved from the same `REACT_APP_API_ENV` the API is, so a staging build
 * hands out staging links and cannot quietly hand out live ones.
 */

import FORM_HOSTS from "./formHosts.json";
import FORM_LINK_HOSTS from "./formLinkHosts.json";
import APP_HOSTS from "./environments.json";
import { API_ENVIRONMENT } from "../api";

/** Origin only, no trailing slash - callers append paths that always begin
 *  with one. */
export const FORMS_BASE_URL: string = (
  process.env.REACT_APP_FORMS_BASE_URL?.trim() || FORM_HOSTS[API_ENVIRONMENT]
).replace(/\/+$/, "");

/**
 * The two addresses a form host serves.
 *
 * Fixed paths rather than a bare origin, so the enquiry form and the
 * pre-consultation form are told apart by the URL alone and neither has to be
 * guessed at from what else is in it. Whose forms these are is not in the path
 * at all — it is the subdomain they are served from.
 *
 * `/i` is the prefix a pre-consultation token hangs off — `/i/<token>`. Short
 * because that link is sent to one person and typed by none, and separate from
 * `/form` because the enquiry form is public and this one is single-use.
 *
 * Kept here rather than in `routes/paths`, which is the dashboard's table and
 * is never loaded on a form host.
 */
export const FORM_PATHS = {
  /** The practitioner's public enquiry form. */
  enquiry: "/form",
  /** Prefix only — the token is the segment after it. */
  preconsultation: "/i",
} as const;

const hostnameOf = (origin: string): string => {
  try {
    return new URL(origin).hostname.toLowerCase();
  } catch {
    return "";
  }
};

/**
 * Renders the forms site rather than the dashboard when this is on - for a
 * developer working on the forms without a second host to serve them from.
 *
 * Off unless it is set, so a plain `npm start` is the dashboard exactly as it
 * was. `REACT_APP_FORMS_PREVIEW=true` in `.env` is what turns localhost into
 * the forms site.
 */
export const FORMS_PREVIEW =
  process.env.REACT_APP_FORMS_PREVIEW?.trim().toLowerCase() === "true";

/** `https://{name}.naurist.com/` — the shape of a link, before there is one.
 *  Declared up here because the apex read out of it is what decides whether a
 *  hostname is a practitioner's; see `practitionerLabel`. */
export const FORM_LINK_TEMPLATE: string =
  process.env.REACT_APP_FORM_LINK_TEMPLATE?.trim() ||
  FORM_LINK_HOSTS[API_ENVIRONMENT];

/**
 * Whether the browser is on the forms site.
 *
 * Read from the address bar rather than from the build, because the wildcard
 * and the dashboard's own address land on the same server and are answered by
 * the same files. It is the only thing keeping the two products apart, and it
 * is a whitelist in both directions: only the dashboard's own hostname gets
 * the dashboard, and only a hostname that parses as somebody's name gets the
 * forms.
 *
 * So `rohan.naurist.com/dashboard` is not a login screen — the dashboard's
 * screens are not registered on a form host at all, and that path falls through
 * to Rohan's enquiry form. The reverse holds too: `/form` is not a route on
 * the dashboard host.
 *
 * The reserved labels below are what stops the whitelist overlapping. Without
 * them `app.naurist.com` would parse as a practitioner called "app" and serve
 * a form to somebody trying to sign in.
 */
export const isFormsHostname = (hostname: string): boolean => {
  const here = hostname.toLowerCase();

  // A shared host, where the name is in the path.
  if (SHARED_FORM_HOSTS.includes(here)) return true;

  // Or a practitioner's own subdomain, where it is in the hostname.
  return !!practitionerLabel(here);
};

/**
 * The same question of the address bar. Split from the pure one above so the
 * rule can be tested against a hostname without standing up a browser — this
 * is the single decision that separates the two products, and it is not one to
 * leave unexercised.
 */
export const isFormsHost = (): boolean => {
  if (FORMS_PREVIEW) return true;
  if (typeof window === "undefined") return false;
  return isFormsHostname(window.location.hostname);
};

/* ── The practitioner's own subdomain ──────────────────────────────────── */

/** The apex out of one link template — `naurist.com` from
 *  `https://{name}.naurist.com/`. */
const apexOf = (template: string): string =>
  hostnameOf(template.replace("{name}", "x")).replace(/^x./, "");

/**
 * Every apex a practitioner's form may hang off, longest first.
 *
 * **All of them, not just this build's.** That is the point, and it was once
 * the other way round: the apex came from `FORM_LINK_TEMPLATE`, which follows
 * `API_ENVIRONMENT`, so a bundle built for production knew only `naurist.com`
 * — and served on `rohan.naurist.openuidev.com` it read the hostname as
 * nobody's, mounted the dashboard, and sent a practitioner's client to a login
 * screen.
 *
 * Which backend a bundle talks to and which product an address asks for are two
 * unrelated questions. The first is settled at build time and has to be: a
 * staging build must not hand out live links. The second is a fact about the
 * address bar, and reading it must not depend on the first — the same files
 * answer on every one of these wildcards.
 *
 * `localhost` is here for the same reason. The backend hands a developer a
 * `share_url` of `http://ayushisahu.localhost:3000/`, which is a practitioner
 * subdomain like any other and would otherwise open the dashboard. Plain
 * `localhost` is not a subdomain of `.localhost`, so `npm start` is still the
 * dashboard.
 *
 * Longest first, so where two apexes overlap the deeper one wins and the label
 * left over stays a single one rather than swallowing a dot.
 */
const FORM_LINK_DOMAINS: string[] = Array.from(
  new Set(
    [
      ...Object.values(FORM_LINK_HOSTS as Record<string, string>),
      process.env.REACT_APP_FORM_LINK_TEMPLATE?.trim() || "",
    ]
      .filter(Boolean)
      .map(apexOf)
      .filter(Boolean)
      .concat("localhost")
  )
).sort((a, b) => b.length - a.length);

/**
 * Every shared forms host — `connect.naurist.com` and its counterparts — where
 * the name is in the path rather than in the hostname. Read across all
 * environments for the same reason the apexes above are.
 */
const SHARED_FORM_HOSTS: string[] = Array.from(
  new Set(
    [...Object.values(FORM_HOSTS as Record<string, string>), FORMS_BASE_URL]
      .map(hostnameOf)
      .filter(Boolean)
  )
);

/**
 * The subdomains under those apexes which are not somebody's form.
 *
 * The dashboard's own is the one that matters — `app.naurist.com` sits under
 * the same domain as every practitioner's link, and reading it as a
 * practitioner called "app" would serve the forms site to somebody trying to
 * log in. The rest are the names an infrastructure claims before anybody gets
 * to choose one.
 *
 * Every environment's, not just this build's, and for the reason given above.
 *
 * A slug this list holds should also be refused at the point it is claimed;
 * that is the backend's to enforce, and this is the second line of it. See
 * `RESERVED_LINK_NAMES`, the longer list a claim is actually checked against.
 */
const RESERVED_LABELS = new Set(
  [
    ...Object.values(APP_HOSTS as Record<string, string>),
    ...Object.values(FORM_HOSTS as Record<string, string>),
    FORMS_BASE_URL,
  ]
    .map((origin) => hostnameOf(origin).split(".")[0])
    .filter(Boolean)
    .concat("www", "api", "admin", "mail", "staging")
);

/**
 * The practitioner's name out of a hostname, or `""` where the hostname is not
 * one of theirs.
 *
 * One label deep and no further: `rohan.naurist.com` is Rohan's,
 * `a.b.naurist.com` is nobody's. A slug cannot contain a dot, so anything
 * deeper is not a name this app handed out.
 */
export const practitionerLabel = (hostname: string): string => {
  const here = hostname.toLowerCase();
  const apex = FORM_LINK_DOMAINS.find((domain) => here.endsWith(`.${domain}`));
  if (!apex) return "";

  const label = here.slice(0, -(apex.length + 1));
  if (!label || label.includes(".")) return "";

  return RESERVED_LABELS.has(label) ? "" : label;
};

/**
 * Whose form this browser is on, read off the address bar.
 *
 * On a practitioner's own subdomain this is the whole answer, and it outranks
 * anything in the path: `rohan.naurist.com/form` is Rohan's form, and so is
 * `rohan.naurist.com/anything-else` — the host has said whose it is, so no
 * path can say otherwise.
 *
 * `""` on the shared host and on localhost, where the hostname holds no name
 * to read. That is the case `/:handle` in `FormsRoutes` covers and the one the
 * enquiry page falls back to the path for; a name in neither place is a link
 * the page treats as not available.
 */
export const formHandleFromHost = (): string => {
  if (typeof window === "undefined") return "";
  return practitionerLabel(window.location.hostname.toLowerCase());
};

/* ── The link a practitioner hands out ─────────────────────────────────────
   Everything above answers "is this browser on the forms site, and whose?".
   The subdomain is how it answers: `/api/v1/enquiry-connect-link/` hands back
   a `share_url` with the name in the hostname — `http://ayushisahu.localhost:3000/`
   on a developer machine and `https://ayushisahu.naurist.com/` in production —
   and the wildcard that catches those points at this same bundle. The
   pre-consultation link is the same hostname with `/i/<token>` after it.

   `connect.naurist.com/<name>` is the older shape, one host with the name
   after the slash. `isFormsHost` still reads it and `/:handle` in
   `FormsRoutes` still serves it, so links handed out before the wildcard
   existed keep working; nothing new points there.

   What is below is only about drawing and reading addresses, never about
   inventing one. One template per environment, with `{name}` standing where
   the name goes, and it draws the empty box and nothing else: the moment a
   link exists the backend has sent the whole address, and `affixesOf` is what
   the box is drawn from after that. Nothing here assembles an address to be
   copied — the server knows where it serves and this does not.          */

/**
 * The fixed text either side of the name, for the input the name is typed
 * into: the practitioner types `diksha-kumari`, not the whole address.
 *
 * Split off the template rather than written out again — the box is a picture
 * of the link, and a picture that can disagree with the link is worse than no
 * picture.
 */
export const formLinkAffixes = (): { prefix: string; suffix: string } => {
  const [prefix = "", suffix = ""] = FORM_LINK_TEMPLATE.split("{name}");
  return { prefix, suffix };
};

/** The host out of any address, or `""` where it is not one yet. */
export const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

/**
 * The fixed text either side of the slug in an address the backend built.
 *
 * Preferred over `formLinkAffixes` wherever there is a stored link, and the
 * reason is the whole point of `share_url`: the template above is this app's
 * guess at where forms are served from, and the backend knows. Drawing the box
 * from the address that was actually handed back means the picture and the
 * copied link cannot disagree — even on a developer's machine, where the
 * address is something like `http://ayushisahu.localhost:3000/` that no
 * template here would ever have produced.
 *
 * `null` where the slug is not in the address at all, which leaves the caller
 * with the template rather than with a box built out of a bad split.
 */
export const affixesOf = (
  shareUrl: string,
  slug: string
): { prefix: string; suffix: string } | null => {
  if (!slug) return null;
  const at = shareUrl.indexOf(slug);
  if (at < 0) return null;

  return {
    prefix: shareUrl.slice(0, at),
    suffix: shareUrl.slice(at + slug.length),
  };
};
